// Report charts, built on recharts (the same library the salon dashboard uses).
//
// Colour note: charts deliberately do NOT use --f7-theme-color. The brand accent
// is a red-orange (#f94e2b) and on this page red already means "down" (the delta
// chips), so a red trend line reads as a loss whichever way it points.
import {
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export const CHART_COLOR = '#2f6fed';
export const CHART_COLOR_SOFT = 'rgba(47,111,237,0.25)';
const MUTED = '#8e8e93';

/** recharts hands the tooltip a loose ValueType; narrow it without casting. */
const tooltipFormatter = (format: (value: number) => string) => (value: unknown) =>
  format(typeof value === 'number' ? value : Number(value) || 0);

const AXIS_TICK = { fontSize: 11, fill: MUTED };

const tooltipStyle = {
  contentStyle: {
    borderRadius: 10,
    border: '1px solid rgba(142,142,147,0.25)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
    fontSize: 12,
    padding: '6px 10px',
    background: 'var(--f7-card-bg-color,#fff)',
  },
  labelStyle: { color: MUTED, marginBottom: 2 },
};

/** Axis-less mini line for a KPI card - shape only, no scale. */
export function Sparkline({ values, height = 36 }: { values: number[]; height?: number }) {
  if (values.length < 2) return <div style={{ height }} />;
  const data = values.map((value, i) => ({ i, value }));
  return (
    <div style={{ height, marginTop: 8 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line
            type="monotone"
            dataKey="value"
            stroke={CHART_COLOR}
            strokeWidth={1.75}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Full trend chart with dated axis and hover readout. */
export function TrendChart({
  points,
  height = 240,
  formatValue,
}: {
  points: { label: string; value: number }[];
  height?: number;
  formatValue: (value: number) => string;
}) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <XAxis
            dataKey="label"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            minTickGap={24}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={64}
            tickFormatter={formatValue}
          />
          <Tooltip {...tooltipStyle} formatter={tooltipFormatter(formatValue)} />
          <Line
            type="monotone"
            dataKey="value"
            name=""
            stroke={CHART_COLOR}
            strokeWidth={2}
            dot={{ r: 2.5, strokeWidth: 0, fill: CHART_COLOR }}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Column chart; `highlightIndex` picks out the peak bar. */
export function ColumnChart({
  points,
  height = 200,
  formatValue,
  highlightIndex,
}: {
  points: { label: string; value: number }[];
  height?: number;
  formatValue: (value: number) => string;
  highlightIndex?: number;
}) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <XAxis
            dataKey="label"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            minTickGap={16}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={64}
            tickFormatter={formatValue}
          />
          <Tooltip
            {...tooltipStyle}
            cursor={{ fill: 'rgba(142,142,147,0.12)' }}
            formatter={tooltipFormatter(formatValue)}
          />
          <Bar dataKey="value" name="" radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {points.map((p, i) => (
              <Cell key={p.label} fill={i === highlightIndex ? CHART_COLOR : CHART_COLOR_SOFT} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Horizontal share row (channel split, day-parts, top sellers). Not a chart -
 *  a labelled proportion bar, the same shape salon uses for its top-services
 *  table, so it stays hand-rolled rather than a recharts layout. */
export function BarRow({
  label,
  value,
  max,
  valueText,
}: {
  label: string;
  value: number;
  max: number;
  valueText: string;
}) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ padding: '6px 0' }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 3 }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        <span style={{ flexShrink: 0, color: MUTED, marginLeft: 8 }}>{valueText}</span>
      </div>
      <div
        style={{
          background: 'rgba(142,142,147,0.2)',
          borderRadius: 4,
          height: 8,
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: CHART_COLOR }} />
      </div>
    </div>
  );
}
